'use client';

import { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { LuArrowRight } from 'react-icons/lu';

// Hero + side-by-side feature carousel.
//
// Animation strategy mirrors src/components/Features/index.tsx:
//   - The carousel track uses `transform: translate3d(...)` instead of
//     scrollLeft so the GPU compositor handles the motion.
//   - Mutable state lives in refs; the rAF loop is started once with empty
//     dependencies. No more teardown-and-rebuild storm on hover.
//   - Delta-time integration keeps the visible speed identical on 60 Hz
//     and 120/144 Hz monitors.
//   - The loop bails when document.visibilityState !== 'visible' so a
//     backgrounded tab doesn't snap on return.

const SCROLL_PX_PER_SEC = 60;
const MAX_FRAME_DELTA_SEC = 0.032;
const DRAG_VELOCITY_MULTIPLIER = 1.5;
const DRAG_THRESHOLD_PX = 5;
const CLICK_THRESHOLD_MS = 200;

const highlightWords = ['Professional', 'Innovative', 'Reliable', 'Trusted'];

const features = [
  {
    image: '/images/features/land-survey-solutions.jpg',
    overlayColor: 'from-blue-600/80',
    title: 'Land Survey Solutions',
    description:
      'Professional land subdivision, transfer services, beacon installation, and area confirmation.',
    services: [
      'Land Subdivision',
      'Land Transfer',
      'Beacon Works',
      'Area Confirmation',
    ],
    path: '/public/services/land-survey',
  },
  {
    image: '/images/features/gis-services.jpg',
    overlayColor: 'from-emerald-600/80',
    title: 'GIS Services',
    description:
      'Advanced geospatial solutions including mapping, spatial analysis, and topographic surveys.',
    services: [
      'Mapping Services',
      'Spatial Analysis',
      'Geo-marketing',
      'Topographic Survey',
    ],
    path: '/public/services/gis-mapping',
  },
  {
    image: '/images/features/property-management.jpg',
    overlayColor: 'from-orange-600/80',
    title: 'Property Management',
    description:
      'Complete property solutions from sales to management, ensuring your investments are maintained.',
    services: [
      'Land Sales',
      'Real Estate',
      'Rental Management',
      'Villa Management',
    ],
    path: '/public/services/property-management',
  },
  {
    image: '/images/features/cyber-services.jpg',
    overlayColor: 'from-purple-600/80',
    title: 'Cyber Services',
    description:
      'Modern digital solutions with high-quality stationery supplies and cyber cafe services.',
    services: [
      'Stationery Supplies',
      'Electronics & Accessories',
      'Printing Services',
      'Computer Training',
    ],
    path: '/public/services/cyber-services',
  },
  {
    image: '/images/pages/qwetu-gas/storefront.jpg',
    overlayColor: 'from-orange-600/80',
    title: 'Qwetu Gas Supplier',
    description:
      'Cylinder refills, kits, and accessories with free home delivery — every brand, every size, fast.',
    services: [
      'Cylinder Refills',
      'New Cylinder Kits',
      'Stoves & Accessories',
      'Free Home Delivery',
    ],
    path: '/public/services/qwetu-gas-supply',
  },
] as const;

const tripleFeatures = [...features, ...features, ...features];

export default function HeroStart() {
  const router = useRouter();
  const [hasScrolled, setHasScrolled] = useState(false);
  const [currentWord, setCurrentWord] = useState(0);

  // Carousel refs
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const offsetRef = useRef(0);
  const isPausedRef = useRef(false);
  const isDraggingRef = useRef(false);
  const directionRef = useRef(1);

  // Drag refs
  const dragStartXRef = useRef(0);
  const dragStartOffsetRef = useRef(0);
  const draggedRef = useRef(false);
  const clickStartRef = useRef(0);

  // Hide the scroll-indicator after the page is scrolled.
  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Rotate the highlighted hero word. Visibility gate prevents a tab
  // switch from desynchronising the cycle counter.
  useEffect(() => {
    const timer = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      setCurrentWord((prev) => (prev + 1) % highlightWords.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const applyOffset = () => {
    const track = trackRef.current;
    if (!track) return;
    const copyWidth = track.scrollWidth / 3;
    if (copyWidth > 0) {
      let offset = offsetRef.current;
      if (offset <= -copyWidth) offset += copyWidth;
      else if (offset >= 0) offset -= copyWidth;
      offsetRef.current = offset;
    }
    track.style.transform = `translate3d(${offsetRef.current}px, 0, 0)`;
  };

  // rAF loop — started once, never resubscribes.
  useEffect(() => {
    let raf = 0;
    let lastTs = 0;
    const tick = (ts: number) => {
      if (document.visibilityState !== 'visible') {
        lastTs = 0;
        raf = requestAnimationFrame(tick);
        return;
      }
      if (!isPausedRef.current && !isDraggingRef.current && lastTs) {
        const dt = Math.min((ts - lastTs) / 1000, MAX_FRAME_DELTA_SEC);
        offsetRef.current += directionRef.current * SCROLL_PX_PER_SEC * dt;
        applyOffset();
      }
      lastTs = ts;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  // Position in the middle copy after layout.
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    offsetRef.current = -track.scrollWidth / 3;
    applyOffset();
  }, []);

  const handleViewportEnter = () => {
    isPausedRef.current = true;
  };
  const handleViewportLeave = () => {
    isPausedRef.current = false;
    isDraggingRef.current = false;
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    isDraggingRef.current = true;
    draggedRef.current = false;
    clickStartRef.current = Date.now();
    dragStartXRef.current = e.clientX;
    dragStartOffsetRef.current = offsetRef.current;
    (e.currentTarget as HTMLDivElement).setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    const dx = (e.clientX - dragStartXRef.current) * DRAG_VELOCITY_MULTIPLIER;
    if (Math.abs(dx) > DRAG_THRESHOLD_PX) {
      draggedRef.current = true;
      directionRef.current = dx > 0 ? -1 : 1;
    }
    offsetRef.current = dragStartOffsetRef.current + dx;
    applyOffset();
  };

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    isDraggingRef.current = false;
    try {
      (e.currentTarget as HTMLDivElement).releasePointerCapture(e.pointerId);
    } catch {
      // releasePointerCapture throws if pointer was never captured. Harmless.
    }
  };

  const handleCardClick = (path: string) => {
    const clickDuration = Date.now() - clickStartRef.current;
    if (!draggedRef.current && clickDuration < CLICK_THRESHOLD_MS) {
      router.push(path);
    }
  };

  const scrollToContact = () => {
    document
      .getElementById('contact')
      ?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className='relative min-h-screen bg-gray-900'>
      <div className='absolute top-0 left-0 right-0 h-1 bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050] z-10' />
      <div className='absolute inset-0 bg-linear-to-b from-gray-900/95 via-gray-900/50 to-gray-900' />

      <div className='absolute inset-0 overflow-hidden'>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMTIxMjEiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzBoMmwyLTJWMGgtMnYzMHoiLz48cGF0aCBkPSJNMzAgMGgydjJIMzB6TTAgMGgydjJIMHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-5" />

        {/* Floating gradient orbs — desktop only. Below lg they are off
            because backdrop-blur + multiple large filters tank mobile fps. */}
        <div className='hidden lg:block absolute top-1/4 right-1/4 h-72 w-72 rounded-full bg-[#00B0F0]/20 blur-3xl animate-float-slow' />
        <div className='hidden lg:block absolute bottom-1/4 left-1/4 h-64 w-64 rounded-full bg-[#FFA500]/20 blur-3xl animate-float-delayed' />
        <div className='hidden lg:block absolute top-1/2 left-1/2 h-56 w-56 rounded-full bg-[#00B050]/20 blur-3xl animate-float' />
      </div>

      <div className='relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 min-h-screen flex flex-col justify-center pt-16 sm:pt-20 lg:pt-0'>
        <div className='flex flex-col lg:flex-row lg:items-center lg:gap-8 h-full py-8 lg:py-0'>
          {/* Hero copy */}
          <div className='lg:w-1/2 flex flex-col justify-center mb-8 lg:mb-0'>
            <div className='max-w-xl mx-auto lg:mx-0 text-center lg:text-left'>
              <h1 className='mb-4 text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl'>
                <span className='block'>Transforming Spaces</span>
                <span className='mt-2 block'>Empowering Growth</span>
                <span className='relative mt-4 inline-block'>
                  <span className='absolute -inset-1 blur-xl bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050] opacity-30' />
                  <span className='relative bg-linear-to-r from-[#00B0F0] via-[#FFA500] to-[#00B050] bg-clip-text text-transparent'>
                    {highlightWords[currentWord]} Solutions
                  </span>
                </span>
              </h1>

              <p className='mt-4 max-w-2xl text-lg sm:text-xl text-gray-300 leading-relaxed mx-auto lg:mx-0'>
                Your comprehensive partner for land surveying, property
                management, and digital solutions. Experience excellence in
                every service we deliver.
              </p>

              <div className='mt-8 flex justify-center lg:justify-start'>
                <button
                  onClick={scrollToContact}
                  className='group inline-flex items-center gap-2 rounded-full bg-linear-to-r from-[#00B0F0] to-[#0078A8] px-6 py-3 text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:shadow-[#00B0F0]/25'
                >
                  <span className='font-medium'>Get Started Today</span>
                  <LuArrowRight className='h-5 w-5 transition-transform duration-300 group-hover:translate-x-1' />
                </button>
              </div>
            </div>
          </div>

          {/* Carousel */}
          <div className='lg:w-1/2'>
            <div className='relative'>
              <div className='text-center mb-2 sm:mb-4'>
                <h2 className='text-xl sm:text-2xl font-bold text-white'>
                  Our Professional Services
                </h2>
              </div>

              <div
                ref={viewportRef}
                className='relative h-[300px] sm:h-[350px] lg:h-[450px] overflow-hidden cursor-grab active:cursor-grabbing rounded-lg select-none'
                onMouseEnter={handleViewportEnter}
                onMouseLeave={handleViewportLeave}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              >
                <div
                  ref={trackRef}
                  className='flex gap-3 sm:gap-4 md:gap-6 py-2 sm:py-4 px-2 sm:px-4 h-full will-change-transform'
                  style={{ transform: 'translate3d(0,0,0)' }}
                >
                  {tripleFeatures.map((feature, idx) => (
                    <div
                      key={idx}
                      className='group relative isolate shrink-0 w-[250px] sm:w-[280px] md:w-80 cursor-pointer'
                      onClick={() => handleCardClick(feature.path)}
                    >
                      <div className='relative h-[250px] sm:h-[280px] md:h-[300px] lg:h-[350px] overflow-hidden rounded-xl transition-transform duration-300 group-hover:scale-[1.02]'>
                        <Image
                          src={feature.image}
                          alt={feature.title}
                          fill
                          sizes='(max-width: 640px) 250px, (max-width: 768px) 280px, 320px'
                          quality={75}
                          priority={idx < 5}
                          draggable={false}
                          className='object-cover transition-transform duration-500 group-hover:scale-110'
                        />

                        <div
                          className={`absolute inset-0 bg-linear-to-b ${feature.overlayColor} to-gray-900/90 opacity-80 group-hover:opacity-90 transition-opacity duration-300`}
                        />

                        <div className='relative h-full p-3 sm:p-4 md:p-6 flex flex-col justify-end'>
                          <div className='mb-2 sm:mb-4'>
                            <h3 className='text-base sm:text-lg md:text-xl font-bold text-white mb-1 sm:mb-2 transform transition-transform duration-500 group-hover:-translate-y-1'>
                              {feature.title}
                            </h3>
                            <p className='text-gray-200 text-xs sm:text-sm transform transition-all duration-500 group-hover:-translate-y-1 opacity-80 group-hover:opacity-100 line-clamp-3 sm:line-clamp-none'>
                              {feature.description}
                            </p>
                          </div>

                          <div className='transform translate-y-4 opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100'>
                            <span className='flex items-center gap-1 sm:gap-2 text-white'>
                              <span className='text-xs sm:text-sm font-medium'>
                                Learn More
                              </span>
                              <LuArrowRight className='h-3 w-3 sm:h-4 sm:w-4 transition-transform duration-300 group-hover:translate-x-1' />
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className='mt-1 sm:mt-2 text-center'>
                <span className='text-gray-400 text-xs sm:text-sm'>
                  <span className='inline-block animate-pulse-slow'>←</span>{' '}
                  Swipe to explore{' '}
                  <span className='inline-block animate-pulse-slow'>→</span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`absolute bottom-8 left-1/2 -translate-x-1/2 z-20 transition-opacity duration-500 ${
          hasScrolled ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        <div className='flex flex-col items-center'>
          <span className='text-[#00B0F0] text-sm mb-2 font-medium'>
            Scroll to explore more
          </span>
          <div className='w-6 h-10 border-2 border-[#00B0F0] rounded-full p-1'>
            <div className='w-1.5 h-1.5 bg-[#00B0F0] rounded-full animate-scroll-down mx-auto' />
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes float {
          0% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
          100% { transform: translateY(0) translateX(0); }
        }
        @keyframes float-delayed {
          0% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(20px) translateX(-10px); }
          100% { transform: translateY(0) translateX(0); }
        }
        @keyframes float-slow {
          0% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(15px) translateX(15px); }
          100% { transform: translateY(0) translateX(0); }
        }
        .animate-float { animation: float 15s ease-in-out infinite; }
        .animate-float-delayed { animation: float-delayed 18s ease-in-out infinite; }
        .animate-float-slow { animation: float-slow 20s ease-in-out infinite; }
      `}</style>
    </div>
  );
}
